/**
 * 프로덕션 v1 문서 → v2 스테이지 문서.
 *
 * 상담 시작·집 구조·진행하기 6단계는 이미 Firestore 에 v1 로 저장돼 있고 프로덕션이
 * 그걸 그린다. 그 문서들의 **질문 내용은 손대지 않는다** — 문항·검증·분기가 오래
 * 다듬어진 것이라 다시 적으면 그때부터 두 벌이 갈라진다. 여기서 하는 일은 두 가지다.
 *
 *   1. `toAssetDocument` 로 v2 껍데기를 씌운다. 질문 트리는 `QuestionSequence` 자산
 *      아래로 그대로 들어가고, 그리는 것은 여전히 기존 Inquiry 엔진이다.
 *   2. 어댑터에만 있던 것을 문서에 적는다 — 무엇으로 채워지는지(`data`), 끝날 때 무엇을
 *      부르는지(`onSubmit` 의 `callAction`), 그 다음 어디로 가는지(`exit`).
 *
 * 2번이 이 파일의 존재 이유다. v1 문서는 "저장하기" 를 표현할 수 없어서 마지막 전이가
 * 죄다 `placeholder-auto` 터미널로 끝나고, 실제 API 호출은 어댑터 `onComplete` 안에
 * 숨어 있었다. 문서만 봐서는 이 화면이 무엇을 저장하는지 알 수 없었다.
 *
 * `callAction` 이름은 `myVisitCounselApi` 의 메서드명과 그대로 맞춘다. 프리뷰는 목
 * 구현을, 프로덕션은 실제 API 를 같은 이름에 등록한다.
 */
import { toAssetDocument, type AssetDocument, type DataSource, type FlowDocument } from "@miso/design-system";

import {
  ARRIVAL_ADDRESS_PREFILL_DATA,
  DEPARTURE_PREFILL_DATA,
  transportPrefillData,
} from "../data/prefill";
import type { ProgressSubStageKind } from "../routes/counselStages";
import { PROGRESS_DOCUMENT_IDS, STAGE_DOCUMENT_IDS } from "./counselFullFlow";

import counselStartV1 from "./v1/counsel-start-flow.json";
import houseStructureV1 from "./v1/house-structure-record-flow.json";
import progressMovingDateV1 from "./v1/progress-moving-date.json";
import progressDepartureTransportV1 from "./v1/progress-departure-transport.json";
import progressArrivalAddressV1 from "./v1/progress-arrival-address.json";
import progressArrivalTransportV1 from "./v1/progress-arrival-transport.json";
import progressPackingV1 from "./v1/progress-packing.json";
import progressCustomerRequestsV1 from "./v1/progress-customer-requests.json";

function v2(raw: unknown, id: string): AssetDocument {
  return { ...toAssetDocument(raw as FlowDocument), id };
}

/**
 * 마지막 화면이 끝날 때 부를 것.
 *
 * v1 문서의 마지막 전이는 `placeholder-auto` 터미널이다. 이름 그대로 자리만 잡아
 * 두고 아무 화면도 없이 지나가는 터미널이고, 실제로 일어나는 일(저장 API)은 어댑터에
 * 있었다. 그 자리를 `onSubmit` 으로 바꾼다 — 저장하고, 알리고, 다음으로 넘어간다.
 */
function saveOn(
  doc: AssetDocument,
  stepId: string,
  action: { name: string; args?: Record<string, unknown>; toast?: string },
): AssetDocument {
  const step = doc.steps[stepId];
  if (!step || step.kind !== "screen") {
    throw new Error(`화면 스텝이 아니다: ${doc.id}.${stepId}`);
  }
  return {
    ...doc,
    steps: {
      ...doc.steps,
      [stepId]: {
        ...step,
        onSubmit: [
          {
            do: "callAction",
            name: action.name,
            ...(action.args ? { args: action.args as never } : {}),
          },
          ...(action.toast
            ? ([{ do: "toast", tone: "success", text: action.toast }] as const)
            : []),
          { do: "submit" },
        ],
        next: { kind: "exit" },
      },
    },
    // 터미널은 더 이상 쓰이지 않는다. 남겨 두면 문서가 아직 거기로 간다고 읽힌다.
    terminals: {},
  };
}

function withData(doc: AssetDocument, data: DataSource[]): AssetDocument {
  return { ...doc, data };
}

// ── 상담 시작 ─────────────────────────────────────────────────────────────────

/*
 * v1 문서는 주소를 물은 뒤 transform 으로 출발지 payload 를 만들고 `room-record-flow`
 * 로 체인한다. 그런데 프로덕션은 그 체인을 어댑터가 가로채 상담 시작 API 를 부른다
 * (`CounselStartFlowAdapter` 시나리오 A) — 다음 화면을 정하는 건 문서의 체인이 아니라
 * 서버가 돌려준 상담이기 때문이다.
 *
 * 가로채기는 문서 밖의 약속이라 문서만 봐서는 보이지 않았다. 체인을 지우고 그 자리에
 * "저장 중" 화면을 세운다. 화면이 뜨자마자 상담을 시작하고, 서버가 따라잡을 때까지
 * 기다렸다가(`poll`) 문서를 끝낸다. 어댑터의 250ms × 40 폴링이 이 세 줄이다.
 */
const START_SAVING_STEP = "starting";

const counselStart: AssetDocument = (() => {
  const base = v2(counselStartV1, STAGE_DOCUMENT_IDS.start);
  const transform = base.steps.transform1;
  if (!transform || transform.kind !== "transform") {
    throw new Error("counsel-start-flow 의 transform1 을 찾지 못했다");
  }
  return withData(
    {
      ...base,
      steps: {
        ...base.steps,
        transform1: {
          ...transform,
          next: { kind: "goto", target: START_SAVING_STEP },
        },
        [START_SAVING_STEP]: {
          kind: "screen",
          onEnter: [
            {
              do: "callAction",
              name: "startVisitCounsel",
              args: { departureAddress: { $var: "departure" } },
            },
            { do: "toast", tone: "success", text: "상담이 시작되었습니다" },
            {
              do: "poll",
              until: { "==": [{ var: "counsel.state" }, "STARTED"] },
              budgetMs: 10_000,
              minIntervalMs: 250,
              maxIntervalMs: 1_000,
              // 서버가 늦어도 화면을 붙잡아 두지 않는다. 다음 판정이 다시 이 단계를
              // 고르면 사용자는 같은 자리에 서 있게 되고, 넘어갔으면 진행된 것이다.
              onTimeout: [{ do: "submit" }],
            },
            { do: "submit" },
          ],
          layout: {
            type: "Screen",
            props: { variant: "cover" },
            children: [
              {
                type: "Cover",
                props: {
                  emoji: "📝",
                  emojiLabel: "상담 시작",
                  title: "상담을 시작하고 있어요",
                  description: "잠시만 기다려주세요.",
                },
              },
            ],
          },
          next: { kind: "exit" },
        },
      },
    },
    DEPARTURE_PREFILL_DATA,
  );
})();

// ── 집 구조 기록 ──────────────────────────────────────────────────────────────

/*
 * 집 구조만은 문서가 끝나도 단계가 안 넘어간다. 마지막 질문에 답하면 "방 구조가
 * 완성되었어요!" 화면이 뜨고, 거기서 방 이름을 고칠 수 있고, "다음" 을 눌러야 스케치
 * 세션이 열린다. 그 화면은 이미 v2 문서(`house-structure.done`)로 있으므로 터미널을
 * 지우고 그 문서로 체인한다 — 터미널 kind 로 커스텀 화면을 부르던 자리가 사라진다.
 */
const houseStructure: AssetDocument = (() => {
  const base = v2(houseStructureV1, STAGE_DOCUMENT_IDS.houseStructure);
  const transformId = Object.keys(base.steps).find(
    (id) => base.steps[id]?.kind === "transform",
  );
  if (!transformId) throw new Error("house-structure-record-flow 의 transform 을 찾지 못했다");
  const transform = base.steps[transformId];
  if (transform?.kind !== "transform") throw new Error("unreachable");

  return {
    ...base,
    steps: {
      ...base.steps,
      [transformId]: {
        ...transform,
        next: { kind: "chain", target: STAGE_DOCUMENT_IDS.houseStructureDone },
      },
    },
    terminals: {},
  };
})();

// ── 진행하기 6단계 ────────────────────────────────────────────────────────────

/**
 * 진행하기 각 단계의 저장 계약.
 *
 * 어댑터 한 개당 한 줄이다. `args` 는 그 화면의 질문 id 를 그대로 읽는다 — 어댑터가
 * `accumulated[...]` 에서 꺼내 요청 객체를 만들던 부분이고, 어느 답이 어느 필드로
 * 가는지는 문서에 적혀 있어야 할 정보다.
 */
const PROGRESS_SAVE: Record<
  Exclude<ProgressSubStageKind, "moving-types">,
  {
    v1: unknown;
    step: string;
    action: { name: string; args?: Record<string, unknown>; toast?: string };
    data?: DataSource[];
  }
> = {
  "moving-date": {
    v1: progressMovingDateV1,
    step: "pick-date",
    action: {
      name: "recordMovingDate",
      args: { movingDate: { $var: "movingDate" } },
      toast: "이사 날짜가 저장되었습니다",
    },
  },
  "departure-transport": {
    v1: progressDepartureTransportV1,
    step: "transport",
    action: {
      name: "recordDepartureSite",
      args: {
        movingMethod: { $var: "method" },
        hasElevator: { $var: "hasElevator" },
        stairCount: { $var: "stairCount" },
      },
      toast: "출발지 운송수단이 저장되었습니다",
    },
    data: transportPrefillData("departure"),
  },
  "arrival-address": {
    v1: progressArrivalAddressV1,
    step: "details",
    action: {
      name: "recordArrivalSite",
      args: {
        address: { $var: "address" },
        buildingName: { $var: "buildingName" },
        dong: { $var: "dong" },
        unit: { $var: "unit" },
        floor: { $var: "floor" },
      },
      toast: "도착지 주소가 저장되었습니다",
    },
    data: ARRIVAL_ADDRESS_PREFILL_DATA,
  },
  "arrival-transport": {
    v1: progressArrivalTransportV1,
    step: "transport",
    action: {
      name: "recordArrivalSite",
      args: {
        movingMethod: { $var: "method" },
        hasElevator: { $var: "hasElevator" },
        stairCount: { $var: "stairCount" },
      },
      toast: "도착지 운송수단이 저장되었습니다",
    },
    data: transportPrefillData("arrival"),
  },
  packing: {
    v1: progressPackingV1,
    step: "pick",
    action: {
      name: "recordPacking",
      args: { method: { $var: "packingMethod" } },
      toast: "포장 방식이 저장되었습니다",
    },
  },
  "customer-requests": {
    v1: progressCustomerRequestsV1,
    // 두 스텝짜리 문서다. 저장은 마지막 스텝에서 일어난다.
    step: "paid",
    action: {
      name: "recordCustomerRequests",
      args: {
        questions: { $var: "questions" },
        paidQuoteRequests: { $var: "paidQuoteRequests" },
      },
      toast: "요청사항이 저장되었습니다",
    },
  },
};

const progressDocuments = Object.fromEntries(
  Object.entries(PROGRESS_SAVE).map(([sub, spec]) => {
    const id = PROGRESS_DOCUMENT_IDS[sub as ProgressSubStageKind];
    const base = v2(spec.v1, id);
    const saved = saveOn(base, spec.step, spec.action);
    return [sub, spec.data ? withData(saved, spec.data) : saved];
  }),
) as Record<Exclude<ProgressSubStageKind, "moving-types">, AssetDocument>;

export const COUNSEL_START = { doc: counselStart };
export const HOUSE_STRUCTURE = { doc: houseStructure };
export const PROGRESS_DOCUMENTS = progressDocuments;
